import React, { useEffect, useRef, useState } from 'react';
import { UserAvatar } from './UserAvatar';

export const THEMES = [
  { value: 'system', label: 'Auto', title: 'Follow the system appearance' },
  { value: 'light', label: 'Light', title: 'Always light' },
  { value: 'dark', label: 'Dark', title: 'Always dark' },
];

export function ThemeSwitch({ value, onChange, compact = false }) {
  return (
    <div className={`seg theme-switch${compact ? ' theme-switch--menu' : ''}`} role="group" aria-label="Theme">
      {THEMES.map((t) => (
        <button
          key={t.value}
          type="button"
          title={t.title}
          aria-pressed={value === t.value}
          className={value === t.value ? 'on' : ''}
          onClick={() => onChange(t.value)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Topbar avatar menu: profile, appearance, sign out.
 */
export function UserMenu({ session, theme, onThemeChange, onManageProfile, onLogout }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const name = session?.displayName || session?.email || 'Signed in';

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        type="button"
        className="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        title={session?.email || name}
        onClick={() => setOpen((v) => !v)}
      >
        <UserAvatar url={session?.profilePictureUrl} name={name} size={32} />
      </button>

      {open && (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-head">
            <UserAvatar url={session?.profilePictureUrl} name={name} size={40} />
            <div className="user-menu-meta">
              <div className="user-menu-name">{name}</div>
              {session?.email && <div className="dim tiny">{session.email}</div>}
              {session?.memberRole && (
                <div className="dim tiny" style={{ textTransform: 'capitalize' }}>
                  {session.memberRole}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onManageProfile?.();
            }}
          >
            Settings
          </button>

          <div className="user-menu-section">
            <div className="user-menu-label">Appearance</div>
            <ThemeSwitch
              value={theme}
              compact
              onChange={(v) => {
                onThemeChange?.(v);
              }}
            />
          </div>

          {onLogout && (
            <>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-item"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onLogout();
                }}
              >
                Sign out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
